import { Fragment, useMemo } from "react";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import DiagnosisSection from "./diagnosisSection";
import { useFieldArray } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import DottedButton from "components/dottedOutlinedButton";
import { diagnosisTypeName } from "dto/encounter.dto";
import { useEncounter } from "pages/encounter/encounter.provider";
import camelToCapitalizeText from "utils/camelToCapitalize";

export interface DiagnosisFormProps {
    name: diagnosisTypeName;
}

const DiagnosisForm = (props: DiagnosisFormProps) => {
    const { name } = props;

    const { encounterForm } = useEncounter();

    const { control } = encounterForm;

    const { getValues: getDoctValue, setValue: setDoctValue } = encounterForm;

    const { fields, append, swap, remove } = useFieldArray({ name, control });

    const handleOnAdd = () => {
        append({
            id: uuidv4(),
            term: "",
            icd10: []
        });
    };

    const swapDxSection = (
        from: diagnosisTypeName,
        fromIndex: number,
        to: diagnosisTypeName,
        position: "tail" | "head"
    ) => {
        if (from === "diagnosis.principal") {
            const value = getDoctValue(`${from}`)!;
            const prevTo = getDoctValue("diagnosis.comorbidity") ?? [];

            setDoctValue("diagnosis.principal", []);
            setDoctValue("diagnosis.comorbidity", [...value, ...prevTo]);
        } else {
            const value = getDoctValue(`${from}.${fromIndex}`);
            if (to === "diagnosis.principal") {
                const prevFrom = getDoctValue(from) ?? [];
                const prevTo = getDoctValue(to)!;

                setDoctValue("diagnosis.principal", [value]);
                setDoctValue(from, [
                    prevFrom[0],
                    ...prevTo,
                    ...prevFrom.slice(1)
                ]);
            } else {
                const prevTo = getDoctValue(to) ?? [];
                if (position === "tail") {
                    setDoctValue(to, [...prevTo, value]);
                } else {
                    setDoctValue(to, [value, ...prevTo]);
                }
            }
        }
    };

    const swapDx = (from: number, to: number) => {
        const diagnosisOrder = [
            "diagnosis.principal",
            "diagnosis.comorbidity",
            "diagnosis.complication",
            "diagnosis.other",
            "diagnosis.externalCause"
        ] as diagnosisTypeName[];

        const length = getDoctValue(`${name}`).length;

        const diagIndex = diagnosisOrder.findIndex((i) => i === name);
        if (from === 0 && to <= 0) {
            // swap to tail above
            swapDxSection(name, from, diagnosisOrder[diagIndex - 1], "tail");
            remove(from);
        } else if (from === length - 1 && to >= length) {
            // swap to head below
            swapDxSection(name, from, diagnosisOrder[diagIndex + 1], "head");
            remove(from);
        } else {
            swap(from, to);
        }
    };

    const title = useMemo(() => {
        const typeName = camelToCapitalizeText(name.replace("diagnosis.", ""));

        if (["principal", "other"].includes(typeName.toLowerCase()))
            return `${typeName} Diagnosis`;

        return typeName;
    }, [name]);

    return (
        <Grid item container spacing={1}>
            <Grid item xs={12} display={"flex"} alignItems={"flex-start"}>
                <Box display={"flex"} alignItems={"center"} height={40}>
                    <Typography>
                        <b>{title}</b>
                    </Typography>
                </Box>
            </Grid>
            <Grid item container spacing={1} xs={12} alignItems={"center"}>
                {fields.map((item, index) => {
                    return (
                        <Fragment key={item.id}>
                            <DiagnosisSection
                                swap={swapDx}
                                onRemove={() => {
                                    remove(index);
                                }}
                                name={name}
                                nestedIndex={index}
                            />
                        </Fragment>
                    );
                })}

                {name !== "diagnosis.principal" ||
                (fields.length === 0 && name === "diagnosis.principal") ? (
                    <Grid item xs={10}>
                        <DottedButton
                            onClick={() => handleOnAdd()}
                            fullWidth
                            startIcon={<AddIcon />}
                            variant="outlined"
                        >
                            <b>Add More</b>
                        </DottedButton>
                    </Grid>
                ) : null}
            </Grid>
        </Grid>
    );
};

export default DiagnosisForm;
